import { useState, useEffect } from 'react'

/**
 * Standard WebNest responsive breakpoints (max-widths, px).
 * Keep these in sync with the breakpoints used in styles/responsive.css.
 */
export const BREAKPOINTS = {
  tablet: 1024, // tablet / narrow desktop
  mobile: 768,  // phone landscape / small tablet — sidebar collapses to a drawer here
  small: 640,   // phone portrait (large)
  tiny: 480,    // phone portrait (small)
}

/**
 * Subscribe to a CSS media query and return whether it currently matches.
 * SSR-safe (returns false until mounted) and cleans up its listener.
 *
 * @param {string} query - a media query string, e.g. '(max-width: 768px)'
 * @returns {boolean}
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia(query)
    const onChange = (e) => setMatches(e.matches)

    // Sync immediately in case the query changed between render and effect
    setMatches(mql.matches)

    // Safari < 14 uses the deprecated addListener API
    if (mql.addEventListener) mql.addEventListener('change', onChange)
    else mql.addListener(onChange)

    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange)
      else mql.removeListener(onChange)
    }
  }, [query])

  return matches
}

/** True at or below the phone breakpoint (<= 768px) — the sidebar-drawer threshold. */
export function useIsMobile() {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.mobile}px)`)
}

/** True at or below the tablet breakpoint (<= 1024px). */
export function useIsTablet() {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.tablet}px)`)
}

/** True at or below the large-phone breakpoint (<= 640px). */
export function useIsSmall() {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.small}px)`)
}
